import { Clusterer, Map } from 'yandex-maps';
import { getBoundsCenter } from '@lib/map-helpers';
import { Sleeve, SleeveManager } from '@lib/sleeve-manager';
import { getBagPlace } from '@lib/bag-formatting';
import { createWhMarker } from './wh-placemark';
import {
  $agricultureById,
  $agricultureIsLoading,
} from '@@/agriculture/store/agriculture.store';
import { IAgriculture } from '@@/agriculture/ts';
import {
  $zoomModeEnabled,
  onContextMenu,
  selectSleeve,
} from '@widgets/storage-viewer/model';
import type { SleeveDescription } from '@lib/sleeve-manager';
import { IWarehouse, IWarehouseWithBags } from '@@/warehouses/ts';
import { DiContainer } from '@@/di/di-container';
import { ClientStorageService } from '@@/common/services/client-storage.service';
import styles from '@@/agriculture/components/agriculture-hint-paramaters/index.module.sass';
import { $mixedBags } from '@entities/simple-mix/model';
import { default as bunkerIcon } from '@/modules/common/icons/bunker.icon.svg';
import { IBag } from '@@/bags/ts';
import { isNull } from 'lodash-es';
const DEFAULT_VIEWER_SETTINGS = {
  center: [45.04484, 38.97603],
  zoom: 8,
  type: 'yandex#hybrid',
  zoom_default: 8,
  controls: ['zoomControl', 'geolocationControl', 'typeSelector', 'fullscreenControl'],
};
const clientStorageService = DiContainer.get(ClientStorageService);
const VIEWER_SETTINGS = 'VIEWER_SETTINGS';
export class WarehouseMap {
  private _map?: Map;
  private _clusterer?: Clusterer;
  private _containerId?: string;
  private _needRegroup?: boolean;
  private _markers: { [whId: number | string]: ymaps.Placemark } = {};
  private _viewerSettings: typeof DEFAULT_VIEWER_SETTINGS = DEFAULT_VIEWER_SETTINGS;
  private _savedSettings: {
    type: ymaps.MapType;
    zoom: number;
    position: number[];
  } | null = null;
  private _sleeveManager: SleeveManager<SleeveDescription[]> | null = null;
  // private _sleeves: ymaps.Placemark[] = [];
  private _buildedSleeves: Sleeve[] = [];
  // private _descriptions: SleeveDescription[] = [];
  private initViewerSettings() {
    try {
      const storedViewerSettings = clientStorageService.get<string>(VIEWER_SETTINGS);
      this._viewerSettings = storedViewerSettings
        ? JSON.parse(storedViewerSettings)
        : DEFAULT_VIEWER_SETTINGS;
      clientStorageService.set(VIEWER_SETTINGS, this._viewerSettings);
    } catch (error) {
      this._viewerSettings = DEFAULT_VIEWER_SETTINGS;
    }
    if (!$zoomModeEnabled.getState() && this._viewerSettings.zoom_default) {
      this._viewerSettings.zoom = this._viewerSettings.zoom_default;
    }
  }
  private updateViewerSettings = () => {
    const center = this._map?.getCenter() ?? DEFAULT_VIEWER_SETTINGS.center;
    const zoom = this._map?.getZoom() ?? DEFAULT_VIEWER_SETTINGS.zoom;
    const settings = { ...this._viewerSettings, center, zoom };
    if (!$zoomModeEnabled.getState()) {
      settings.zoom_default = zoom;
    }
    clientStorageService.set(VIEWER_SETTINGS, settings);
  };
  init(id: string, container: HTMLElement | null) {
    if (!container) throw new Error('Не задан container');
    this._container = container;
    this._containerId = id;
    this.initViewerSettings();
    const map = new ymaps.Map(this._containerId, this._viewerSettings as any);
    //this._map?.setType('yandex#hybrid');
    this._map = map;
    map.events.add('actionend', this.updateViewerSettings);
    map.behaviors.disable('dblClickZoom');
  }
  dispose() {
    if (this._map) {
      // console.log('Удаляем карту ...');
      this._map.destroy();
    }
    if (this._sleeveManager) this._sleeveManager.dispose();
    this._markers = {};
  }
  resize() {
    // console.log('resize');
    if (this._map) this._map.container.fitToViewport();
  }
  private markAsMixed(description: SleeveDescription, isMixed?: boolean) {
    const color = '#66B28A';
    if (!description.params) return;
    if (isMixed) {
      description.isMixed = true;
      description.params.unshift({
        label: 'В бункере',
        value: `<img src="${bunkerIcon}"/>`,
      });
    } else if (description.params[0]?.label == 'В бункере') {
      description.params.shift();
    }
    return description;
  }
  private buildSleeves(wh: IWarehouseWithBags) {
    const agricultureIsLoading = $agricultureIsLoading.getState();
    const agricultureById = $agricultureById.getState();
    if (!this._sleeveManager || agricultureIsLoading) return;
    wh.bags.forEach((b) => {
      if (!b.fillingDate) b.values = [];
    });
    // this._sleeves.forEach((s) => {
    //   this._map?.geoObjects.remove(s);
    // });
    for (let i = this._buildedSleeves.length - 1; i >= 0; i--) {
      const sleeve = this._buildedSleeves[i];
      const newSleeveIndex = wh.bags.findIndex((b) => sleeve.id == b.id);
      const newSleeve = wh.bags[newSleeveIndex];
      if (!newSleeve) {
        this._map?.geoObjects.remove(sleeve.marker);
        this._buildedSleeves.splice(i, 1);
        this._sleeveManager.removeSleeve(sleeve);
        continue;
      }
      if (
        (!sleeve.params?.length && newSleeve.values.length) ||
        (sleeve.params?.length && !newSleeve.fillingDate) ||
        sleeve.params?.some((p) => {
          if (p.label == 'В бункере') return;
          let parameter = newSleeve.values.find((v) => p.label == v.name);
          if (!parameter || isNull(parameter.value)) {
            return true;
          }
          return parameter.value + parameter.units != p.value;
        })
      ) {
        this._map?.geoObjects.remove(sleeve.marker);
        this._buildedSleeves.splice(i, 1);
        wh.bags[newSleeveIndex].nochange = false;
      } else {
        // wh.bags.splice(newSleeveIndex, 1);
        // console.log('not changed');
        wh.bags[newSleeveIndex].nochange = true;
      }
    }
    // this._buildedSleeves.forEach((s) => {
    //   this._map?.geoObjects.remove(s.marker);
    // });
    //
    // this._buildedSleeves = [];
    // console.log('bags', wh.bags);
    // this._sleeves = [];
    const currentUrl = window.location.href;
    const urlParams = new URLSearchParams(new URL(currentUrl).search);
    const bags = urlParams.get('bags')?.split(',');
    const descriptions: SleeveDescription[] = wh.bags.map((bag) => {
      const culture = agricultureById[bag.agricultureId as number] as
        | IAgriculture
        | undefined;
      // тут мы смотрим, если пользователь пришел смотреть выбранные рукава(по url параметру bags), помечаем их цветом, а если нет оставлем как есть
      const color = !bags
        ? culture
          ? culture.color
          : '#ffffff'
        : bags.includes(String(bag.id))
        ? culture
          ? culture.color
          : '#66B28A'
        : '#ccc';
      const params = bag.values.map(({ name, value, units }) => ({
        label: name,
        value: `${value}${units}`,
      }));
      bag.weight && params.push({ label: 'Вес', value: bag.weight + 'т' });

      culture?.name &&
        params.unshift({ label: 'Культура', value: String(culture?.name) });
      //Проверка на имя культуры чтобы не показывалось в пустых рукавах
      culture?.name &&
        params.unshift({
          label: 'Номер на складе (ваш учёт)',
          value: bag.customName ?? ' ',
        });
      //Проверка на имя культуры чтобы не показывалось в пустых рукавах
      culture?.name &&
        params.unshift({ label: 'Описание объема', value: bag.description ?? ' ' });
      // const isMixed = $mixedBags.getState().includes(bag.id);
      // if (isMixed) {
      //   params.unshift({ label: 'В бункере', value: `<img src="${bunkerIcon}"/>` });
      // }
      const description: SleeveDescription = {
        params,
        weight: bag.weight,
        id: bag.id,
        label: getBagPlace(bag.id).toString(),
        position: bag.position,
        color: color,
        strokeColor: color,
        noChange: bag.nochange as any,
        hintClassName: styles.root,
        opacity: bag.agricultureId ? 1 : 0.4,
        events: {
          click: ({ id }) => selectSleeve(id.toString()),
          context: (sleeve, e) => {
            let id = sleeve.id.toString();
            let coords = e.get('pagePixels');
            setTimeout(() => {
              onContextMenu({
                coords,
                id,
              });
            }, 200);
          },
        },
      };
      this.markAsMixed(description, $mixedBags.getState().includes(bag.id));
      return description;
    });
    // console.log('already builded', this._buildedSleeves.length);
    const sleeves = !this._buildedSleeves.length
      ? this._sleeveManager.buildSleevesByStartEndPoints(descriptions)
      : this._sleeveManager.buildSeparateSleeves(
          descriptions.filter((d) => !d.noChange),
        );
    // this._buildedSleeves = sleeves;
    sleeves.forEach((s) => {
      const index = this._buildedSleeves.findIndex((bs) => bs.id == s.id);
      if (index < 0) {
        // console.log('adding sleeve', s);
        this._map?.geoObjects.add(s.marker);
        return this._buildedSleeves.push(s);
      }
      this._buildedSleeves[index] = s;
    });
    // sleeves.forEach((s) => {
    //   // this._sleeves.push(s.marker);
    //   this._map?.geoObjects.add(s.marker);
    // });
    // console.log('final', this._buildedSleeves);
  }
  /**
   * addWarehouse
   *
   * @description Добавляет метку склада на карту
   */
  public addWarehouse(
    warehouse: IWarehouse,
    addToGeoObjects: boolean = false, // Изменено с true на false
    length: number | null,
    onClick?: () => void,
    onDblClick?: () => void,
    onContextMenu?: (coords: Array<number>) => void,
  ) {
    if (!this._map || !warehouse.marker) return;
    const placeMark = createWhMarker(warehouse);
    this._markers[warehouse.id as number] = placeMark;
    if (onClick) {
      placeMark.events.add('click', onClick);
    }
    if (onDblClick && warehouse.type == 2) {
      placeMark.events.add('dblclick', onDblClick);
    }
    if (onContextMenu) {
      placeMark.events.add('contextmenu', (e) => {
        onContextMenu(e.get('pagePixels'));
      });
    }
    // Добавляем группировку после добавления всех складов
    if (length) {
      if (Object.keys(this._markers).length > length) {
        this.groupPlacemarks();
      }
    }
  }
  /**
   * groupPlacemarks
   * @description Группирует близко находящиеся метки в кластер
   */
  private _regroupIfNecessary() {
    if (this._needRegroup && Object.keys(this._markers).length >= 2) {
      this.groupPlacemarks();
      this._needRegroup = false;
    }
  }
  public groupPlacemarks() {
    if (!this._map) {
      return;
    }
    if (Object.keys(this._markers).length < 2) {
      return;
    }

    this._clusterer = new ymaps.Clusterer({
      groupByCoordinates: false,
      gridSize: 300,
      maxZoom: 50,
      minClusterSize: 2,
    });
    // Получаем все метки
    const placemarks: ymaps.Placemark[] = Object.values(this._markers);
    // Удаляем метки по одной
    placemarks.forEach((marker) => {
      if (this._map) {
        this._map.geoObjects.remove(marker);
      }
    });
    // Добавляем метки в кластерер
    this._clusterer.add(placemarks);
    // Добавляем кластерер на карту
    if (this._map) {
      this._map.geoObjects.add(this._clusterer);
    }
  }
  public checkMarkersCoordinates() {
    const coordinates = Object.values(this._markers).map((marker) =>
      marker.geometry.getCoordinates(),
    );
    // Проверяем, различаются ли координаты
    const areDifferent = coordinates.some((coord, index) => {
      if (index === 0) return true;
      return coord[0] !== coordinates[0][0] || coord[1] !== coordinates[0][1];
    });
  }
  public finalizeGrouping() {
    this._regroupIfNecessary();
  }
  public test(whs: IWarehouse[]) {
    //console.log(ymaps.util.bounds)
    // const placemarks: Placemark[] = [];
    // for (const key in this._markers) {
    //   placemarks.push(this._markers[key]);
    // }
    // const boundsArray = placemarks.map(marker => marker.geometry.getBounds());
    // const unitedBounds = ymaps.util.bounds.union.apply(null, boundsArray);
    // const res = unitedBounds.containsBounds(boundsArray[0]) && unitedBounds.containsBounds(boundsArray[1]);
    // console.log('res: ', res);
  }
  // перерисовка одного рукава заданного
  public markSleeve(info: IBag, isMixed?: boolean) {
    if (!this._sleeveManager || !this._map) return;
    const sleeveId = this._buildedSleeves.findIndex((s) => s.id == info.id);
    const sleeve = this._buildedSleeves[sleeveId];
    if (sleeve) {
      sleeve.marker.options.set(
        'preset',
        isMixed ? 'sleeve#mixed' + sleeve.id : 'sleeve#simple' + sleeve.id,
      );
    }
  }
  // public updateSleeve() {
  //   if (!this._sleeveManager || !this._map) return;
  // }
  /**
   * updateZoomPlacemarks
   */
  public updateZoomPlacemarks(wh: IWarehouseWithBags) {
    // console.log('update zoom');
    if (this._map && this._sleeveManager) {
      this.buildSleeves(wh);
    }
  }
  /**
   * enterZoomMode
   */
  public enterZoomMode(wh: IWarehouseWithBags) {
    if (!this._map) return;
    if (this._map.geoObjects.getLength()) {
      // console.log('geo objects', this._map.geoObjects);
    }
    // this._sleeves = [];
    this._buildedSleeves = [];
    this._map.geoObjects.removeAll();
    this._savedSettings = {
      type: this._map.getType(),
      zoom: this._map.getZoom(),
      position: this._map.getCenter(),
    };
    const field = new ymaps.Polygon(
      [wh.position],
      {},
      {
        strokeColor: '#FFF1B8',
        fill: false,
        strokeWidth: 2,
        strokeStyle: 'solid',
        interactivityModel: 'default#transparent',
      },
    );
    this._map.geoObjects.add(field);
    const bounds = field.geometry?.getBounds() as number[][];
    const center = getBoundsCenter(bounds);
    this._map.setBounds(bounds);
    this._map.setCenter(center);
    this._map.setType('yandex#hybrid');
    this._sleeveManager = new SleeveManager(this._map);
    this._sleeveManager.sleeveLength = wh.bagsSize as number;
    this.buildSleeves(wh);
  }
  /**
   * leaveZoomMode
   */

  public leaveZoomMode() {
    if (!this._map) return;

    this._map.geoObjects.removeAll();
    this._sleeveManager?.dispose();

    const allIds = Object.keys(this._markers);
    for (const id of allIds) {
      this._map.geoObjects.add(this._markers[id]);
    }
    // Получаем все метки
    const placemarks: ymaps.Placemark[] = Object.values(this._markers);
    // Удаляем метки по одной
    placemarks.forEach((marker) => {
      if (this._map) {
        this._map.geoObjects.remove(marker);
      }
    });
    // Добавляем метки в кластерер
    this._clusterer.add(placemarks);
    // Добавляем кластерер на карту
    if (this._map) {
      this._map.geoObjects.add(this._clusterer);
    }
    if (this._savedSettings) {
      this._map.setZoom(this._savedSettings.zoom);
      this._map.setType('yandex#hybrid');
      this._map.setCenter(this._savedSettings.position);
    }
  }
  /**
   * moveToWarehouse
   *
   * @description Перемещает камеру на маркер выбранного склада
   */
  public moveToWarehouse(warehouseId: number) {
    if (!this._map || !this._markers) return;
    const warehouseMarker = this._markers[warehouseId];
    if (!warehouseMarker) {
      throw new Error('Маркера для этого склада не существует');
    }
    const newCenter =
      warehouseMarker.geometry?.getCoordinates() ?? DEFAULT_VIEWER_SETTINGS.center;
    this._map.setCenter(newCenter, this._map.getZoom(), { duration: 300 });
  }
  // getContextMenu(warehouse: warehouseModel.Warehouse) {}
  /**
   * removeWarehouse
   *
   */
  public removeWarehouse(warehouseId: number) {
    if (!this._map) {
      return;
    }
    if (this._markers[warehouseId]) {
      this._map.geoObjects.remove(this._markers[warehouseId]);
      delete this._markers[warehouseId];
    }
  }
  public removeSleeve(bagId: string) {
    const sleeveId = this._buildedSleeves.findIndex((s) => s.id == bagId);
    const sleeve = this._buildedSleeves[sleeveId];
    this._map?.geoObjects.remove(sleeve.marker);
  }
  /**
   * clearMap
   */
  public clear() {
    if (this._map) {
      this._markers = {};
      // this._sleeves = [];
      this._buildedSleeves = [];
      this._map.geoObjects.removeAll();
    }
  }
}
export const map = new WarehouseMap();
